import { Colors } from "@jsq/ds/styles";

/**
 * Shared styling constants for DataVault AG Grid cell editors
 * Uses design system tokens for consistent styling across all cell editors
 */

export const CELL_EDITOR_STYLES = {
  container: {
    width: "100%",
    height: "100%",
  },
  input: {
    width: "100%",
    border: `1px solid ${Colors.Neutral300}`,
    borderRadius: "4px",
    padding: "8px",
    fontSize: "14px",
    height: "100%",
    textAlign: "right" as const,
    outline: "none",
  },
  inputError: {
    border: `1px solid ${Colors.Danger600}`,
  },
} as const;

export const ALLOWED_KEYS = [
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
];

const CLIPBOARD_KEYS = ["c", "v", "x", "a"];

export const isAllowedKey = (event: KeyboardEvent): boolean => {
  if (ALLOWED_KEYS.includes(event.key)) {
    return true;
  }

  if (!event.ctrlKey && !event.metaKey) {
    return false;
  }

  return CLIPBOARD_KEYS.includes(event.key.toLowerCase());
};

/**
 * Shared validation state update utility
 * Applies consistent error styling across all cell editors
 */
export const updateValidationState = (
  inputElement: HTMLInputElement,
  validationError: string | null,
): void => {
  if (validationError) {
    Object.assign(inputElement.style, CELL_EDITOR_STYLES.inputError);
    inputElement.title = validationError;
  } else {
    inputElement.style.border = CELL_EDITOR_STYLES.input.border;
    inputElement.title = "";
  }
};

export const handleNumericPaste = (
  event: ClipboardEvent,
  inputElement: HTMLInputElement,
  cleanPattern: RegExp,
  onValueChange: (value: string) => void,
): void => {
  event.preventDefault();

  const pastedText = event.clipboardData?.getData("text");
  if (!pastedText) {
    return;
  }

  const cleanedText = pastedText.replace(cleanPattern, "");

  const start = inputElement.selectionStart || 0;
  const end = inputElement.selectionEnd || 0;
  const currentValue = inputElement.value;

  const newValue = currentValue.substring(0, start) + cleanedText + currentValue.substring(end);

  inputElement.value = newValue;
  onValueChange(newValue);

  const newCursorPosition = start + cleanedText.length;
  inputElement.setSelectionRange(newCursorPosition, newCursorPosition);

  inputElement.dispatchEvent(new Event("input"));
};

/**
 * Options for the unified keyboard handler utility
 * Supports both native DOM events and React synthetic events
 */
export interface CellEditorKeyboardHandlerOptions {
  inputElement?: HTMLInputElement | HTMLSelectElement;
  initialValue: string;
  currentValueSetter: (value: string) => void;
  api: { stopEditing: (cancel?: boolean) => void };
}

/**
 * Unified keyboard handler for AG Grid cell editors
 * Handles standard Enter (save) and Escape (cancel) behavior
 * Compatible with both native KeyboardEvent and React.KeyboardEvent
 * Maintains AG Grid cell editor interface compatibility
 */
export const handleCellEditorKeyboard = (
  event: KeyboardEvent | React.KeyboardEvent,
  options: CellEditorKeyboardHandlerOptions,
): void => {
  const { inputElement, initialValue, currentValueSetter, api } = options;

  if (event.key === "Enter") {
    event.preventDefault();
    event.stopPropagation();
    api.stopEditing();
  } else if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();

    // Reset to initial value for DOM-based editors
    if (inputElement && "value" in inputElement) {
      inputElement.value = initialValue;
    }
    // Always update the component state
    currentValueSetter(initialValue);
    api.stopEditing(true);
  }
};

export type DropEffectType = "none" | "copy" | "link" | "move";
export type EffectAllowedType =
  | "none"
  | "copy"
  | "copyLink"
  | "copyMove"
  | "link"
  | "linkMove"
  | "move"
  | "all"
  | "uninitialized";

export const DATA_TRANSFER_DEFAULTS = {
  DROP_EFFECT: "none",
  EFFECT_ALLOWED: "none",
  EFFECT_ALLOWED_UNINITIALIZED: "uninitialized",
} as const;
